/**
 * Material 3 Expressive Shape System
 * Implements the 35 new shapes introduced in Material 3 Expressive
 * Optimized for Bonapisca with marine-inspired variants
 */

// Base Material 3 Shape Scale
export const M3_BASE_SHAPES = {
  none: '0px',
  'extra-small': '4px',
  small: '8px',
  medium: '12px',
  large: '16px',
  'extra-large': '28px',
  full: '1000px',
} as const;

// Material 3 Expressive Extended Shapes (35 new shapes)
export const M3_EXPRESSIVE_SHAPES = {
  // Basic geometric shapes
  'curved-small': '20px',
  'curved-medium': '24px',
  'curved-large': '32px',
  'curved-extra-large': '40px',
  
  // Asymmetric shapes for expressive design
  'organic-small': '12px 8px 16px 6px',
  'organic-medium': '18px 12px 24px 10px',
  'organic-large': '26px 18px 32px 14px',
  
  // Marine-inspired shapes for Mediterranean context
  'wave-gentle': '16px 24px 20px 12px',
  'wave-strong': '24px 32px 28px 16px',
  'coral-soft': '14px 18px 22px 10px',
  'coral-bold': '20px 28px 32px 16px',
  'shell-small': '8px 16px 12px 20px',
  'shell-medium': '12px 24px 18px 30px',
  'shell-large': '18px 36px 26px 42px',
  
  // Geometric variations
  'diamond-small': '0px 12px 0px 12px',
  'diamond-medium': '0px 18px 0px 18px',
  'diamond-large': '0px 24px 0px 24px',
  'hexagon-small': '8px 16px 8px 16px',
  'hexagon-medium': '12px 24px 12px 24px',
  'hexagon-large': '16px 32px 16px 32px',
  
  // Functional shapes for UI elements
  'button-gentle': '20px',
  'button-soft': '16px',
  'button-firm': '12px',
  'card-relaxed': '24px',
  'card-cozy': '20px',
  'card-snug': '16px',
  
  // Avatar and image shapes
  'avatar-soft': '20px',
  'avatar-gentle': '16px',
  'avatar-friendly': '12px',
  'image-editorial': '28px 8px 28px 8px',
  'image-artistic': '32px 12px 32px 12px',
  'image-gallery': '20px 32px 20px 32px',
  
  // Decorative elements
  'decoration-subtle': '8px 20px 12px 16px',
  'decoration-bold': '16px 32px 24px 28px',
  'decoration-dynamic': '12px 28px 20px 36px',
} as const;

// Combined shape tokens
export const MATERIAL3_SHAPES = {
  ...M3_BASE_SHAPES,
  ...M3_EXPRESSIVE_SHAPES,
} as const;

// Shape categories for easy selection
export const SHAPE_CATEGORIES = {
  basic: ['none', 'extra-small', 'small', 'medium', 'large', 'extra-large', 'full'],
  curved: ['curved-small', 'curved-medium', 'curved-large', 'curved-extra-large'],
  organic: ['organic-small', 'organic-medium', 'organic-large'],
  marine: ['wave-gentle', 'wave-strong', 'coral-soft', 'coral-bold', 'shell-small', 'shell-medium', 'shell-large'],
  geometric: ['diamond-small', 'diamond-medium', 'diamond-large', 'hexagon-small', 'hexagon-medium', 'hexagon-large'],
  functional: ['button-gentle', 'button-soft', 'button-firm', 'card-relaxed', 'card-cozy', 'card-snug'],
  avatar: ['avatar-soft', 'avatar-gentle', 'avatar-friendly'],
  image: ['image-editorial', 'image-artistic', 'image-gallery'],
  decorative: ['decoration-subtle', 'decoration-bold', 'decoration-dynamic'],
} as const;

// Type definitions
export type Material3Shape = keyof typeof MATERIAL3_SHAPES;
export type ShapeCategory = keyof typeof SHAPE_CATEGORIES;

// CSS custom properties generator
export const generateShapeCSS = () => {
  const cssVars: Record<string, string> = {};
  
  Object.entries(MATERIAL3_SHAPES).forEach(([key, value]) => {
    cssVars[`--md-shape-${key.replace(/_/g, '-')}`] = value;
  });
  
  return cssVars;
};

// Utility functions
export const getShapeValue = (shape: Material3Shape): string => {
  return MATERIAL3_SHAPES[shape];
};

export const getShapesByCategory = (category: ShapeCategory): Material3Shape[] => {
  return SHAPE_CATEGORIES[category] as unknown as Material3Shape[];
};

// Shape morphing configurations for animations
export const SHAPE_MORPHING_CONFIGS = {
  gentle: {
    duration: 400,
    easing: 'cubic-bezier(0.2, 0.0, 0, 1.0)',
  },
  smooth: {
    duration: 300,
    easing: 'cubic-bezier(0.0, 0.0, 0, 1.0)',
  },
  snappy: {
    duration: 200,
    easing: 'cubic-bezier(0.3, 0.0, 0.8, 0.15)',
  },
} as const;

export type ShapeMorphingConfig = keyof typeof SHAPE_MORPHING_CONFIGS;